import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SessionManager from '../../utils/sessionManager';
import useCredits from '../../hooks/useCredits';

export default function Navbar() {
  const navigate = useNavigate();
  const [solutionsDropdownOpen, setSolutionsDropdownOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  
  // Credit system
  const { balance, loading: creditsLoading } = useCredits();

  useEffect(() => {
    // Check for current session
    const session = SessionManager.getCurrentSession();
    setCurrentSession(session);
    
    // Close dropdowns when clicking outside
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setSolutionsDropdownOpen(false);
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative z-10 px-4 sm:px-6 pt-6 sm:pt-8 pb-3" style={{ backgroundColor: 'white' }}>
      <nav className="mx-auto flex max-w-7xl items-center justify-between">
        {/* Logo */}
        <div className="flex items-center">
          <button 
            onClick={() => navigate('/')}
            className="font-bold cursor-pointer bg-transparent border-none"
            style={{
              fontSize: "clamp(24px, 5vw, 35px)",
              background: "linear-gradient(180deg, #DE4396 0%, #0D1C9F 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent"
            }}
          >
            Aptirix
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
          aria-label="Toggle menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {mobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>

        {/* Desktop Navigation Links */}
        <div className="hidden lg:flex items-center gap-6 xl:gap-8 text-[16px] xl:text-[18px]">
          {/* Solutions Dropdown */}
          <div className="relative dropdown-container">
            <button 
              onClick={() => setSolutionsDropdownOpen(!solutionsDropdownOpen)}
              className="flex items-center gap-2 text-[#6a6a6a] transition-colors hover:text-[#000000]"
              style={{ 
                fontFamily: 'Inter',
                fontWeight: 500,
                WebkitFontSmoothing: 'antialiased',
                letterSpacing: 'normal',
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Solutions
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4z"
                />
              </svg>
            </button>
            
            {solutionsDropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-56 rounded-lg border border-gray-200 bg-white shadow-xl z-[9999]">
                <div className="p-2">
                  <button
                    onClick={() => {
                      navigate('/enhance-cv');
                      setSolutionsDropdownOpen(false);
                    }}
                    className="flex w-full items-center gap-3 rounded-md px-3 py-3 text-left text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                      <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                      </svg>
                    </div>
                    <div>
                      <div className="font-medium">Enhanced CV</div>
                      <div className="text-sm text-gray-500">Improve your resume with AI</div>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/courses');
                      setSolutionsDropdownOpen(false);
                    }}
                    className="flex w-full items-center gap-3 rounded-md px-3 py-3 text-left text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                      </svg>
                    </div>
                    <div>
                      <div className="font-medium">Courses</div>
                      <div className="text-sm text-gray-500">Learn new skills and advance</div>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <button 
            onClick={() => navigate('/jobs')}
            className="text-[#6a6a6a] transition-colors hover:text-[#000000]"
            style={{ 
              fontFamily: 'Inter',
              fontWeight: 500,
              WebkitFontSmoothing: 'antialiased',
              letterSpacing: 'normal',
              background: 'none',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Jobs
          </button>
          <button 
            onClick={() => navigate('/ai-trainer')}
            className="text-[#6a6a6a] transition-colors hover:text-[#000000]"
            style={{ 
              fontFamily: 'Inter',
              fontWeight: 500,
              WebkitFontSmoothing: 'antialiased',
              letterSpacing: 'normal',
              background: 'none',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            AI Trainer
          </button>
          <button 
            onClick={() => navigate('/about')}
            className="text-[#6a6a6a] transition-colors hover:text-[#000000]"
            style={{ 
              fontFamily: 'Inter',
              fontWeight: 500,
              WebkitFontSmoothing: 'antialiased',
              letterSpacing: 'normal',
              background: 'none',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            About Us
          </button>
          
          {/* Authentication Buttons - Show profile dropdown when logged in */}
          {!currentSession ? (
            <>
              <button
                onClick={() => navigate('/login')}
                className="transition-opacity hover:opacity-80"
                style={{ 
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  WebkitFontSmoothing: 'antialiased',
                  letterSpacing: 'normal',
                  background: 'linear-gradient(180deg, #DE4396 0%, #0D1C9F 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Log in
              </button>
              <button
                onClick={() => navigate('/signup')}
                className="transition-opacity hover:opacity-90"
                style={{ 
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  WebkitFontSmoothing: 'antialiased',
                  letterSpacing: 'normal',
                  backgroundColor: '#000000',
                  color: '#FFFFFF',
                  borderRadius: '18px',
                  padding: '12px 24px',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Sign Up
              </button>
            </>
          ) : (
            /* Show user profile dropdown when logged in */
            <div className="relative dropdown-container">
              <button
                onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                className="flex items-center gap-2 transition-opacity hover:opacity-80"
                style={{ 
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  WebkitFontSmoothing: 'antialiased',
                  letterSpacing: 'normal',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  borderRadius: '25px',
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  cursor: 'pointer'
                }}
              >
                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                  {currentSession.profile?.first_name?.charAt(0) || currentSession.email?.charAt(0) || 'U'}
                </div>
                <span>{currentSession.profile?.first_name || 'Profile'}</span>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4z" />
                </svg>
              </button>
              
              {/* Profile Dropdown */}
              {profileDropdownOpen && (
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-xl border border-gray-200 py-4 z-[9999] max-h-[80vh] overflow-y-auto">
                  {/* User Info Header */}
                  <div className="px-6 pb-4 border-b border-gray-100">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white text-lg font-bold">
                        {currentSession.profile?.first_name?.charAt(0) || currentSession.email?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 text-lg">
                          {currentSession.profile?.first_name || 'User'}
                        </div>
                        <div className="text-gray-500 text-sm">
                          {currentSession.email}
                        </div>
                      </div>
                    </div>
                    <div className="text-purple-600 font-medium text-sm">
                      Candidate
                    </div>
                  </div>
                  
                  {/* Credit Balance Section */}
                  <div className="px-6 py-3 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.736 6.979C9.208 6.193 9.696 6 10 6c.304 0 .792.193 1.264.979a1 1 0 001.715-1.029C12.279 4.784 11.232 4 10 4s-2.279.784-2.979 1.95c-.285.475-.507 1-.67 1.55H6a1 1 0 000 2h.013a9.358 9.358 0 000 1H6a1 1 0 100 2h.351c.163.55.385 1.075.67 1.55C7.721 15.216 8.768 16 10 16s2.279-.784 2.979-1.95a1 1 0 10-1.715-1.029c-.472.786-.96.979-1.264.979-.304 0-.792-.193-1.264-.979a4.265 4.265 0 01-.264-.521H10a1 1 0 100-2H8.017a7.36 7.36 0 010-1H10a1 1 0 100-2H8.472c.08-.185.167-.36.264-.521z" />
                          </svg>
                        </div>
                        <span className="text-xs font-medium text-gray-600">Credits</span>
                      </div>
                      <span className="text-sm font-bold text-purple-600">
                        {creditsLoading ? '...' : balance}
                      </span>
                    </div>
                    {/* Progress Bar */}
                    <div className="relative w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="absolute top-0 left-0 h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full transition-all duration-300 credit-shimmer"
                        style={{ width: `${Math.min((balance / 150) * 100, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1 text-center">
                      {balance < 30 ? '⚠️ Low balance' : balance < 100 ? '✓ Good' : '✨ Excellent'}
                    </p>
                  </div>
                  
                  {/* Menu Items */}
                  <div className="py-2">
                    <button
                      onClick={() => {
                        navigate('/profile/view');
                        setProfileDropdownOpen(false);
                      }}
                      className="flex w-full items-center gap-3 px-6 py-3 text-left text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                      </svg>
                      <span className="font-medium">My Profile</span>
                    </button>
                    
                    <button
                      onClick={() => {
                        navigate('/my-courses');
                        setProfileDropdownOpen(false);
                      }}
                      className="flex w-full items-center gap-3 px-6 py-3 text-left text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                      </svg>
                      <span className="font-medium">My Courses</span>
                    </button>
                    
                    <button
                      onClick={() => {
                        navigate('/my-applications');
                        setProfileDropdownOpen(false);
                      }}
                      className="flex w-full items-center gap-3 px-6 py-3 text-left text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                      </svg>
                      <span className="font-medium">My Applications</span>
                    </button>
                    
                    <button
                      onClick={() => {
                        navigate('/my-reports');
                        setProfileDropdownOpen(false);
                      }}
                      className="flex w-full items-center gap-3 px-6 py-3 text-left text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                      </svg>
                      <span className="font-medium">My Reports</span>
                    </button>
                  </div>
                  
                  {/* Logout */}
                  <div className="border-t border-gray-100 pt-2">
                    <button
                      onClick={() => {
                        SessionManager.clearAllSessions();
                        navigate('/');
                        window.location.reload();
                      }}
                      className="flex w-full items-center gap-3 px-6 py-3 text-left text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" />
                      </svg>
                      <span className="font-medium">Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden mt-4 pb-4 border-t border-gray-200">
          <style>{`
            @keyframes shimmer {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(100%); }
            }
            .credit-shimmer::after {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
              animation: shimmer 2s infinite;
            }
          `}</style>
          <div className="flex flex-col gap-4 pt-4">
            {/* Solutions Dropdown Mobile */}
            <div>
              <button 
                onClick={() => setSolutionsDropdownOpen(!solutionsDropdownOpen)}
                className="flex items-center justify-between w-full text-left px-2 py-2 text-gray-700 hover:text-gray-900"
              >
                <span className="font-medium">Solutions</span>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4z" />
                </svg>
              </button>
              {solutionsDropdownOpen && (
                <div className="ml-4 mt-2 space-y-2">
                  <button
                    onClick={() => {
                      navigate('/enhance-cv');
                      setMobileMenuOpen(false);
                      setSolutionsDropdownOpen(false);
                    }}
                    className="flex w-full items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded-md"
                  >
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                      </svg>
                    </div>
                    <div>
                      <div className="font-medium text-sm">Enhanced CV</div>
                      <div className="text-xs text-gray-500">Improve your resume</div>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/courses');
                      setMobileMenuOpen(false);
                      setSolutionsDropdownOpen(false);
                    }}
                    className="flex w-full items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded-md"
                  >
                    <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                      </svg>
                    </div>
                    <div>
                      <div className="font-medium text-sm">Courses</div>
                      <div className="text-xs text-gray-500">Learn new skills</div>
                    </div>
                  </button>
                </div>
              )}
            </div>

            <button 
              onClick={() => { navigate('/jobs'); setMobileMenuOpen(false); }}
              className="text-left px-2 py-2 text-gray-700 hover:text-gray-900 font-medium"
            >
              Jobs
            </button>
            <button 
              onClick={() => { navigate('/ai-trainer'); setMobileMenuOpen(false); }}
              className="text-left px-2 py-2 text-gray-700 hover:text-gray-900 font-medium"
            >
              AI Trainer
            </button>
            <button 
              onClick={() => { navigate('/about'); setMobileMenuOpen(false); }}
              className="text-left px-2 py-2 text-gray-700 hover:text-gray-900 font-medium"
            >
              About Us
            </button>

            {/* Auth Buttons Mobile */}
            <div className="flex flex-col gap-3 pt-4 border-t border-gray-200">
              {!currentSession ? (
                <>
                  <button
                    onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                    className="w-full py-3 px-4 text-center font-semibold rounded-lg border-2 border-gray-900 text-gray-900 hover:bg-gray-50"
                  >
                    Log in
                  </button>
                  <button
                    onClick={() => { navigate('/signup'); setMobileMenuOpen(false); }}
                    className="w-full py-3 px-4 text-center font-semibold rounded-lg bg-gray-900 text-white hover:bg-gray-800"
                  >
                    Sign Up
                  </button>
                </>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-3 px-2 py-2">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white font-bold">
                      {currentSession.profile?.first_name?.charAt(0) || currentSession.email?.charAt(0) || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 truncate">
                        {currentSession.profile?.first_name || 'User'}
                      </div>
                      <div className="text-sm text-gray-500 truncate">{currentSession.email}</div>
                    </div>
                  </div>
                  
                  {/* Credit Balance - Mobile */}
                  <div className="px-2 py-2 mx-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                          <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.736 6.979C9.208 6.193 9.696 6 10 6c.304 0 .792.193 1.264.979a1 1 0 001.715-1.029C12.279 4.784 11.232 4 10 4s-2.279.784-2.979 1.95c-.285.475-.507 1-.67 1.55H6a1 1 0 000 2h.013a9.358 9.358 0 000 1H6a1 1 0 100 2h.351c.163.55.385 1.075.67 1.55C7.721 15.216 8.768 16 10 16s2.279-.784 2.979-1.95a1 1 0 10-1.715-1.029c-.472.786-.96.979-1.264.979-.304 0-.792-.193-1.264-.979a4.265 4.265 0 01-.264-.521H10a1 1 0 100-2H8.017a7.36 7.36 0 010-1H10a1 1 0 100-2H8.472c.08-.185.167-.36.264-.521z" />
                          </svg>
                        </div>
                        <span className="text-xs font-medium text-gray-600">Credits</span>
                      </div>
                      <span className="text-sm font-bold text-purple-600">
                        {creditsLoading ? '...' : balance}
                      </span>
                    </div>
                    <div className="relative w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="absolute top-0 left-0 h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min((balance / 150) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                  
                  <button
                    onClick={() => { navigate('/profile/view'); setMobileMenuOpen(false); }}
                    className="w-full text-left px-2 py-2 text-gray-700 hover:bg-gray-50 rounded-md"
                  >
                    My Profile
                  </button>
                  <button
                    onClick={() => { navigate('/my-courses'); setMobileMenuOpen(false); }}
                    className="w-full text-left px-2 py-2 text-gray-700 hover:bg-gray-50 rounded-md"
                  >
                    My Courses
                  </button>
                  <button
                    onClick={() => { navigate('/my-applications'); setMobileMenuOpen(false); }}
                    className="w-full text-left px-2 py-2 text-gray-700 hover:bg-gray-50 rounded-md"
                  >
                    My Applications
                  </button>
                  <button
                    onClick={() => { navigate('/my-reports'); setMobileMenuOpen(false); }}
                    className="w-full text-left px-2 py-2 text-gray-700 hover:bg-gray-50 rounded-md"
                  >
                    My Reports
                  </button>
                  <button
                    onClick={() => {
                      SessionManager.clearAllSessions();
                      setMobileMenuOpen(false);
                      navigate('/');
                      window.location.reload();
                    }}
                    className="w-full text-left px-2 py-2 text-red-600 hover:bg-red-50 rounded-md font-medium"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
